// ═══════════════════════════════════════════════════════════════════════════
// PAGE: POST-IT
// Migration S5.3 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════

import {
  buildCreateActionPayload,
  type EditorSavedAction,
} from '../domain/action-create-payload.js';

interface Postit {
  id: number;
  description: string;
  date?: string | null;
  color?: string | null;
  created_by?: number | null;
  reminder_enabled?: boolean;
  // Instant UTC naïf renvoyé par le backend (sans suffixe Z) — on lui
  // ajoute 'Z' avant `new Date()` pour l'afficher en heure locale.
  reminder_at?: string | null;
}

const DEFAULT_REMINDER_TIME = '09:00';

// Largeur des champs date/heure révélés inline sur une ligne de post-it.
// La date doit être assez large pour que le segment ANNÉE (le plus à droite
// de l'input natif) ne soit pas tronqué : à 110px Chrome coupait l'année,
// la rendant non saisissable (bug mai 2026).
const DATE_FIELD_WIDTH = '160px';
const TIME_FIELD_WIDTH = '95px';

// CTA cloche : on veut un contraste FORT entre les deux états.
// Actif  = bouton plein doré (cloche blanche) → saute aux yeux.
// Inactif = discret, atténué (cloche barrée grisée).
const BELL_ACTIVE_STYLE = 'background:#E0A800;border-color:#E0A800;color:#fff';
const BELL_INACTIVE_STYLE = 'background:transparent;color:var(--text3);opacity:.7';

// Heure locale (HH:MM) d'un `reminder_at` (UTC naïf backend), pour
// pré-remplir l'input time. Cf. règle "datetimes UTC backend".
function reminderLocalTime(reminderAt: string | null | undefined): string {
  if (!reminderAt) return DEFAULT_REMINDER_TIME;
  const d = new Date(reminderAt + 'Z');
  if (isNaN(d.getTime())) return DEFAULT_REMINDER_TIME;
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

// Combine une date (YYYY-MM-DD) et une heure (HH:MM) LOCALES en un instant
// UTC ISO (avec Z). Le backend le re-normalise en UTC naïf.
function combineToUtcIso(date: string, time: string): string | null {
  const d = new Date(`${date}T${time}:00`);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

// Ouvre le champ heure (caché par défaut) d'une ligne de post-it et y place
// le focus, pour saisir/ajuster l'heure du rappel.
function revealTimeField(list: HTMLElement, pid: number): void {
  const inp = list.querySelector<HTMLInputElement>(`.pi-time-edit[data-pid="${pid}"]`);
  if (!inp) return;
  inp.style.opacity = '1';
  inp.style.width = TIME_FIELD_WIDTH;
  inp.focus();
}

// Referme le champ heure (la valeur est déjà persistée par ailleurs).
function collapseTimeField(inp: HTMLInputElement): void {
  inp.style.opacity = '0';
  inp.style.width = '0';
}

declare global {
  interface Window {
    renderPostitsPage: typeof renderPostitsPage;
    PI_COLORS: typeof PI_COLORS;
    buildColorPicker: typeof buildColorPicker;
    buildPostitRow: typeof buildPostitRow;
    renderPostitList: typeof renderPostitList;
    wirePostitEvents: typeof wirePostitEvents;
    initPostitDragDrop: typeof initPostitDragDrop;
  }
}

const PI_COLORS: ReadonlyArray<string> = ['yellow', 'orange', 'pink', 'blue', 'green'];

pages.postits = async function () {
  setHeader(
    t('vocabulary.postit_plural'),
    `<button class="btn btn-secondary" id="btn-refresh-postits" title="${esc(t('pagePostits.btnRefreshTitle'))}">${esc(t('pagePostits.btnRefresh'))}</button>`,
  );
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  const uid = await ensureCurrentUserId();
  if (!uid) {
    setContent(`<div class="error">${esc(t('pagePostits.errNotAuthenticated'))}</div>`);
    return;
  }
  try {
    const items = (await api.getPostits()) as Postit[];
    renderPostitsPage(items);
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
};

function renderPostitsPage(items: Postit[]): void {
  const userId = currentUserId();
  let sortByDate = false;

  const getSorted = (): Postit[] => {
    if (!sortByDate) return items;
    return [...items].sort((a, b) => {
      if (!a.date && !b.date) return 0;
      if (!a.date) return 1;
      if (!b.date) return -1;
      return a.date.localeCompare(b.date);
    });
  };

  const html = `
    <div class="mx-auto" style="max-width:640px">
      <div class="card mb-3" id="postit-entry-card" style="padding:12px 16px">
        <!-- RefactoUX mai 2026 : libellé pleine largeur sur la ligne du haut,
             les meta (date + couleur + CTA) regroupées sur la ligne du dessous
             pour laisser plus de place à la saisie principale. -->
        <div style="display:flex;flex-direction:column;gap:8px">
          <input type="text" id="pi-desc" placeholder="${esc(t('pagePostits.phDesc'))}"
            autocomplete="off" style="width:100%;font-size:15px;padding:8px 10px;border:1px solid var(--border2);border-radius:var(--radius)">
          <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">
            <input type="date" id="pi-date" class="text-sm" style="padding:7px 8px;border:1px solid var(--border2);border-radius:var(--radius)" title="${esc(t('pagePostits.titleDateOpt'))}">
            <button type="button" id="pi-new-bell" class="btn btn-secondary btn-icon" title="${esc(t('pagePostits.reminder.newOff'))}">🔕</button>
            <input type="time" id="pi-new-time" class="text-sm" value="${DEFAULT_REMINDER_TIME}"
              style="padding:7px 8px;border:1px solid var(--border2);border-radius:var(--radius);opacity:0;width:0;overflow:hidden;transition:all .15s" title="${esc(t('pagePostits.reminder.titleTime'))}">
            <span class="pi-color-picker" id="pi-new-color-picker" title="${esc(t('pagePostits.titleNewColor'))}">
              ${PI_COLORS.map(
                (col, i) => `
                <button type="button"
                  class="pi-color-swatch${i === 0 ? ' selected' : ''}"
                  data-color="${col}"
                  aria-label="${esc(t('pagePostits.color.' + col))}"
                  title="${esc(t('pagePostits.color.' + col))}"></button>
              `,
              ).join('')}
            </span>
            <button id="btn-pi-add" class="btn btn-primary" style="padding:8px 16px;font-size:15px;margin-left:auto" title="${esc(t('pagePostits.btnAddTitle'))}">${esc(t('pagePostits.btnAdd'))}</button>
          </div>
        </div>
      </div>

      <div class="flex items-center justify-between mb-2">
        <button class="btn btn-secondary btn-sm" id="btn-sort-date" style="font-size:12px">
          ${esc(t('pagePostits.btnSortByDate'))}
        </button>
        <button class="btn btn-secondary btn-sm text-danger" id="btn-clear-all" style="font-size:12px">
          ${esc(t('pagePostits.btnClearAll'))}
        </button>
      </div>

      <div id="postit-list">
        ${
          items.length
            ? items.map((p) => buildPostitRow(p)).join('')
            : `<p class="text-center text-muted text-sm" style="padding:32px 0">${esc(t('pagePostits.emptyHelp'))}</p>`
        }
      </div>
    </div>`;

  setContent(html);

  let newColor = 'yellow';
  document
    .querySelectorAll<HTMLButtonElement>('#pi-new-color-picker .pi-color-swatch')
    .forEach((btn) => {
      btn.addEventListener('click', () => {
        newColor = btn.dataset['color'] || 'yellow';
        document
          .querySelectorAll<HTMLButtonElement>('#pi-new-color-picker .pi-color-swatch')
          .forEach((s) => {
            s.classList.toggle('selected', s.dataset['color'] === newColor);
          });
      });
    });

  // Rappel du NOUVEAU post-it (ligne de saisie). État local : la cloche
  // arme un rappel qui sera posé à la création (date du post-it + heure).
  let newReminderOn = false;
  const newBell = document.getElementById('pi-new-bell') as HTMLButtonElement | null;
  const newTime = document.getElementById('pi-new-time') as HTMLInputElement | null;
  const newDateInput = document.getElementById('pi-date') as HTMLInputElement | null;

  const syncNewReminderUI = (): void => {
    if (newBell) {
      newBell.textContent = newReminderOn ? '🔔' : '🔕';
      // Même contraste fort que sur les lignes existantes (plein doré vs
      // discret atténué).
      newBell.style.background = newReminderOn ? '#E0A800' : 'transparent';
      newBell.style.borderColor = newReminderOn ? '#E0A800' : '';
      newBell.style.color = newReminderOn ? '#fff' : 'var(--text3)';
      newBell.style.opacity = newReminderOn ? '1' : '.7';
      newBell.title = newReminderOn
        ? t('pagePostits.reminder.newOn')
        : t('pagePostits.reminder.newOff');
    }
    if (newTime) {
      newTime.style.opacity = newReminderOn ? '1' : '0';
      newTime.style.width = newReminderOn ? TIME_FIELD_WIDTH : '0';
    }
  };

  newBell?.addEventListener('click', () => {
    void (async () => {
      if (newReminderOn) {
        newReminderOn = false;
        syncNewReminderUI();
        return;
      }
      // Un rappel exige une date : on la demande avant d'armer la cloche.
      if (!newDateInput?.value) {
        toast(t('pagePostits.reminder.needDate'), 'warning');
        newDateInput?.focus();
        return;
      }
      // Abonnement push contextuel, comme sur une ligne existante.
      await window.ensurePushReminders();
      newReminderOn = true;
      syncNewReminderUI();
    })();
  });

  const doCreate = async (): Promise<void> => {
    const descInput = document.getElementById('pi-desc') as HTMLInputElement | null;
    const desc = descInput?.value.trim();
    if (!desc) {
      descInput?.focus();
      return;
    }
    const dateInput = document.getElementById('pi-date') as HTMLInputElement | null;
    const date = dateInput?.value || null;
    let reminderEnabled = false;
    let reminderAt: string | null = null;
    if (newReminderOn) {
      if (!date) {
        toast(t('pagePostits.reminder.needDate'), 'warning');
        dateInput?.focus();
        return;
      }
      reminderAt = combineToUtcIso(date, newTime?.value || DEFAULT_REMINDER_TIME);
      if (!reminderAt) {
        toast(t('common.error'), 'error');
        return;
      }
      reminderEnabled = true;
    }
    try {
      const created = (await api.createPostit({
        description: desc,
        date,
        color: newColor,
        reminder_enabled: reminderEnabled,
        reminder_at: reminderAt,
      })) as Postit;
      items.unshift(created);
      if (descInput) descInput.value = '';
      if (dateInput) dateInput.value = '';
      newReminderOn = false;
      if (newTime) newTime.value = DEFAULT_REMINDER_TIME;
      syncNewReminderUI();
      descInput?.focus();
      renderPostitList(getSorted(), items, userId);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('common.error');
      toast(msg, 'error');
    }
  };

  const descInput = document.getElementById('pi-desc') as HTMLInputElement | null;
  descInput?.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter') void doCreate();
  });
  document.getElementById('btn-pi-add')?.addEventListener('click', () => void doCreate());
  descInput?.focus();

  document.getElementById('btn-sort-date')?.addEventListener('click', () => {
    sortByDate = !sortByDate;
    const btn = document.getElementById('btn-sort-date') as HTMLButtonElement | null;
    if (btn) {
      btn.textContent = sortByDate
        ? t('pagePostits.btnSortManual')
        : t('pagePostits.btnSortByDate');
      btn.style.color = sortByDate ? 'var(--accent)' : '';
      btn.style.borderColor = sortByDate ? 'var(--accent)' : '';
    }
    renderPostitList(getSorted(), items, userId);
  });

  document.getElementById('btn-clear-all')?.addEventListener('click', () => {
    openModal(
      t('pagePostits.clearModalTitle'),
      `<p>${t('pagePostits.clearModalBody')}</p>`,
      async () => {
        try {
          await api.deleteAllPostits();
          closeModal();
          toast(t('pagePostits.toastCleared'));
          items.splice(0, items.length);
          renderPostitList([], items, userId);
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : t('common.error');
          toast(msg, 'error');
        }
      },
      t('pagePostits.btnDeleteAll'),
    );
  });

  const refreshBtn = document.getElementById('btn-refresh-postits') as HTMLButtonElement | null;
  refreshBtn?.addEventListener('click', () => {
    void api.withButton(refreshBtn, async () => {
      try {
        const fresh = (await api.getPostits()) as Postit[];
        items.splice(0, items.length, ...fresh);
        renderPostitList(getSorted(), items, userId);
        toast(t('pagePostits.toastRefreshed'));
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : t('common.error');
        toast(msg, 'error');
      }
    });
  });

  // Bug fix (mai 2026) : `renderPostitList` câble déjà les events sur les
  // lignes via son appel interne à `wirePostitEvents`. Le 2e appel ci-dessous
  // doublait les listeners — chaque clic sur 📅 togglait la visibilité 2 fois
  // (0→1 puis 1→0), résultat : aucun changement apparent. Une fois un post-it
  // créé, `doCreate` rappelait `renderPostitList` sur un DOM neuf et le bug
  // disparaissait. Solution : ne plus appeler `wirePostitEvents` séparément.
  renderPostitList(getSorted(), items, userId);
}

// Persiste l'état du rappel d'un post-it (activation + instant) puis
// re-render la liste. `enabled=true` exige une date sur le post-it.
async function persistReminder(
  p: Postit,
  items: Postit[],
  userId: number | null,
  enabled: boolean,
  time: string,
): Promise<void> {
  let reminderAt: string | null = null;
  if (enabled) {
    if (!p.date) {
      toast(t('pagePostits.reminder.needDate'), 'warning');
      return;
    }
    reminderAt = combineToUtcIso(p.date, time);
    if (!reminderAt) {
      toast(t('common.error'), 'error');
      return;
    }
  }
  try {
    const updated = (await api.updatePostit(p.id, {
      description: p.description,
      date: p.date,
      color: p.color,
      reminder_enabled: enabled,
      reminder_at: reminderAt,
    })) as Postit;
    p.reminder_enabled = updated.reminder_enabled ?? false;
    p.reminder_at = updated.reminder_at ?? null;
    renderPostitList(items, items, userId);
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : t('common.error');
    toast(msg, 'error');
  }
}

function buildColorPicker(pid: number, currentColor: string): string {
  const c = PI_COLORS.includes(currentColor) ? currentColor : 'yellow';
  return `<span class="pi-color-picker" data-pid="${pid}" title="${esc(t('pagePostits.titleColor'))}">
    ${PI_COLORS.map(
      (col) => `
      <button type="button"
        class="pi-color-swatch${col === c ? ' selected' : ''}"
        data-pid="${pid}"
        data-color="${col}"
        aria-label="${esc(t('pagePostits.color.' + col))}"
        title="${esc(t('pagePostits.color.' + col))}"></button>
    `,
    ).join('')}
  </span>`;
}

function buildPostitRow(p: Postit): string {
  const color = p.color || 'yellow';
  const reminderActive = Boolean(p.reminder_enabled && p.reminder_at);
  return `<div class="postit-row flex items-center rounded" data-pid="${p.id}" data-color="${color}"
    style="padding:10px 12px;margin-bottom:6px;gap:10px;
           cursor:default;transition:box-shadow .1s;touch-action:pan-y">
    <span class="pi-handle drag-handle flex-shrink-0" role="button" aria-label="${esc(t('pagePostits.handleAria'))}" style="touch-action:none">⠿</span>
    <div style="flex:1;min-width:0">
      <div class="pi-desc text-md"
        data-pid="${p.id}"
        style="color:var(--text);cursor:text;word-break:break-word"
        contenteditable="true"
        title="${esc(t('pagePostits.titleEdit'))}">${esc(p.description)}</div>
      ${
        p.date || reminderActive
          ? `<div class="text-xs text-muted" style="margin-top:2px;display:flex;flex-wrap:wrap;gap:8px;align-items:center">${
              p.date ? `<span>${esc(fmtDate(p.date))}</span>` : ''
            }${
              reminderActive
                ? `<span class="pi-reminder-chip" data-pid="${p.id}" role="button" tabindex="0" style="cursor:pointer;color:#B8860B;font-weight:600;white-space:nowrap" title="${esc(t('pagePostits.reminder.titleEditTime'))}">🔔&nbsp;${esc(reminderLocalTime(p.reminder_at))}</span>`
                : ''
            }</div>`
          : ''
      }
    </div>
    <div class="pi-actions flex items-center flex-shrink-0" style="gap:6px">
      ${buildColorPicker(p.id, color)}
      <div class="pi-buttons-row flex items-center" style="gap:6px">
        <input type="date" class="pi-date-edit text-xs" data-pid="${p.id}" value="${esc(p.date || '')}"
          style="padding:2px 4px;border:1px solid var(--border);border-radius:4px;
                 opacity:0;width:0;transition:all .15s;overflow:hidden" title="${esc(t('pagePostits.titleDate'))}">
        <button class="btn btn-secondary btn-icon pi-date-btn text-xs" data-pid="${p.id}" title="${esc(t('pagePostits.titleEditDate'))}">📅</button>
        <input type="time" class="pi-time-edit text-xs" data-pid="${p.id}" value="${esc(reminderLocalTime(p.reminder_at))}"
          style="padding:2px 4px;border:1px solid var(--border);border-radius:4px;
                 opacity:0;width:0;transition:all .15s;overflow:hidden" title="${esc(t('pagePostits.reminder.titleTime'))}">
        <button class="btn btn-secondary btn-icon pi-bell-btn text-xs" data-pid="${p.id}"
          style="${reminderActive ? BELL_ACTIVE_STYLE : BELL_INACTIVE_STYLE}" title="${esc(reminderActive ? t('pagePostits.reminder.titleActive', { time: reminderLocalTime(p.reminder_at) }) : t('pagePostits.reminder.titleOff'))}">${reminderActive ? '🔔' : '🔕'}</button>
        <button class="btn btn-secondary btn-sm pi-to-action-btn text-xs text-nowrap" data-pid="${p.id}" title="${esc(t('pagePostits.titleToAction', { object: t('vocabulary.action') }))}">${esc(t('pagePostits.btnToAction', { object: t('vocabulary.action') }))}</button>
        <button class="btn btn-danger btn-icon pi-del-btn" data-pid="${p.id}" title="${esc(t('common.delete'))}">✕</button>
      </div>
    </div>
  </div>`;
}

function renderPostitList(
  displayItems: ReadonlyArray<Postit>,
  items: Postit[],
  userId: number | null,
): void {
  const list = document.getElementById('postit-list');
  if (!list) return;

  list.innerHTML = displayItems.length
    ? displayItems.map((p) => buildPostitRow(p)).join('')
    : `<p class="text-center text-muted text-sm" style="padding:32px 0">${esc(t('pagePostits.empty'))}</p>`;
  wirePostitEvents(items, userId);
}

function wirePostitEvents(items: Postit[], userId: number | null): void {
  const list = document.getElementById('postit-list');
  if (!list) return;

  list.querySelectorAll<HTMLElement>('.pi-desc').forEach((el) => {
    el.addEventListener('blur', () => {
      void (async () => {
        const pid = parseInt(el.dataset['pid'] || '0');
        const p = items.find((x) => x.id === pid);
        if (!p) return;
        const newDesc = (el.textContent || '').trim();
        if (!newDesc) {
          el.textContent = p.description;
          return;
        }
        if (newDesc === p.description) return;
        try {
          const updated = (await api.updatePostit(pid, {
            description: newDesc,
            date: p.date,
          })) as Postit;
          p.description = updated.description;
        } catch (err: unknown) {
          el.textContent = p.description;
          const msg = err instanceof Error ? err.message : t('common.error');
          toast(msg, 'error');
        }
      })();
    });
    el.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        el.blur();
      }
    });
  });

  list.querySelectorAll<HTMLButtonElement>('.pi-date-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const pid = parseInt(btn.dataset['pid'] || '0');
      const dateInput = list.querySelector<HTMLInputElement>(`.pi-date-edit[data-pid="${pid}"]`);
      if (!dateInput) return;
      const visible = dateInput.style.opacity === '1';
      dateInput.style.opacity = visible ? '0' : '1';
      dateInput.style.width = visible ? '0' : DATE_FIELD_WIDTH;
      if (!visible) dateInput.focus();
    });
  });

  list.querySelectorAll<HTMLInputElement>('.pi-date-edit').forEach((inp) => {
    inp.addEventListener('change', () => {
      void (async () => {
        const pid = parseInt(inp.dataset['pid'] || '0');
        const p = items.find((x) => x.id === pid);
        if (!p) return;
        const newDate = inp.value || null;
        // Si un rappel est armé, il suit la date du post-it : on recalcule
        // `reminder_at` (date du post-it + heure du rappel). Sans date, le
        // rappel ne peut plus exister → on le coupe.
        const reminderOn = Boolean(p.reminder_enabled && p.reminder_at);
        const payload: Record<string, unknown> = {
          description: p.description,
          date: newDate,
        };
        if (reminderOn) {
          if (newDate) {
            payload['reminder_enabled'] = true;
            payload['reminder_at'] = combineToUtcIso(newDate, reminderLocalTime(p.reminder_at));
          } else {
            payload['reminder_enabled'] = false;
            payload['reminder_at'] = null;
          }
        }
        try {
          const updated = (await api.updatePostit(pid, payload)) as Postit;
          p.date = updated.date ?? null;
          p.reminder_enabled = updated.reminder_enabled ?? false;
          p.reminder_at = updated.reminder_at ?? null;
          renderPostitList(items, items, userId);
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : t('common.error');
          toast(msg, 'error');
        }
      })();
    });
  });

  list.querySelectorAll<HTMLButtonElement>('.pi-bell-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      void (async () => {
        const pid = parseInt(btn.dataset['pid'] || '0');
        const p = items.find((x) => x.id === pid);
        if (!p) return;
        const currentlyOn = Boolean(p.reminder_enabled && p.reminder_at);
        if (currentlyOn) {
          await persistReminder(p, items, userId, false, DEFAULT_REMINDER_TIME);
          return;
        }
        if (!p.date) {
          toast(t('pagePostits.reminder.needDate'), 'warning');
          const dateInput = list.querySelector<HTMLInputElement>(
            `.pi-date-edit[data-pid="${pid}"]`,
          );
          if (dateInput) {
            dateInput.style.opacity = '1';
            dateInput.style.width = DATE_FIELD_WIDTH;
            dateInput.focus();
          }
          return;
        }
        // Abonnement push contextuel : on demande la permission seulement
        // ici, au moment où l'utilisateur arme un rappel.
        await window.ensurePushReminders();
        // Arme avec l'heure par défaut puis ouvre le champ heure pour ajuster
        // immédiatement. La saisie referme le champ (cf. handler `change`).
        await persistReminder(p, items, userId, true, DEFAULT_REMINDER_TIME);
        revealTimeField(list, pid);
      })();
    });
  });

  // Clic sur le chip « 🔔 HH:MM » (sous le titre) → rouvre le champ heure.
  list.querySelectorAll<HTMLElement>('.pi-reminder-chip').forEach((chip) => {
    const open = (): void => revealTimeField(list, parseInt(chip.dataset['pid'] || '0'));
    chip.addEventListener('click', open);
    chip.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        open();
      }
    });
  });

  list.querySelectorAll<HTMLInputElement>('.pi-time-edit').forEach((inp) => {
    inp.addEventListener('change', () => {
      void (async () => {
        const pid = parseInt(inp.dataset['pid'] || '0');
        const p = items.find((x) => x.id === pid);
        if (!p) return;
        // La persistance re-render la ligne : le champ repart fermé et la
        // valeur s'affiche dans le chip sous le titre.
        await persistReminder(p, items, userId, true, inp.value || DEFAULT_REMINDER_TIME);
      })();
    });
    // Refermer le champ si on quitte sans modifier (la valeur est déjà posée).
    inp.addEventListener('blur', () => collapseTimeField(inp));
  });

  list.querySelectorAll<HTMLButtonElement>('.pi-to-action-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      void (async () => {
        const pid2 = parseInt(btn.dataset['pid'] || '0');
        const p = items.find((x) => x.id === pid2);
        if (!p) return;

        if (p.date) {
          const d = new Date(p.date + 'T00:00:00');
          const [y, m, day] = p.date.split('-').map(Number);
          const valid =
            !isNaN(d.getTime()) &&
            d.getFullYear() === y &&
            d.getMonth() + 1 === m &&
            d.getDate() === day;
          if (!valid) {
            toast(t('pagePostits.errInvalidDate', { date: p.date }), 'error');
            return;
          }
        }

        const [users, statuses, projects] = await Promise.all([
          loadUsers(),
          loadActionStatuses(),
          api.getProjects(),
        ]);

        const template = {
          text: p.description,
          deadline: p.date || null,
          approver_id: p.created_by || currentUserId(),
          priority: 2,
          deadline_revised: null,
          status_id: getDefaultStatusId(),
          progress: 0,
          performers: [],
          consulted: [],
          informed: [],
          origins: [],
          origin: '',
          project_ids: [],
        };

        window.showEditActionModal(
          template as never,
          {
            users: users as never,
            statuses: statuses as never,
            projects: projects as never,
            onSaved: async (data: never, _notes: unknown, notify = true) => {
              try {
                await api.createAction(buildCreateActionPayload(data as EditorSavedAction), {
                  notify,
                });
                toast(t('pagePostits.toastActionCreated'));
              } catch (err: unknown) {
                const msg = err instanceof Error ? err.message : t('common.error');
                toast(msg, 'error');
              }
            },
          } as never,
        );
      })();
    });
  });

  list.querySelectorAll<HTMLButtonElement>('.pi-del-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const pid = parseInt(btn.dataset['pid'] || '0');
      const p = items.find((x) => x.id === pid);
      const label = p ? `"${p.description.slice(0, 40)}"` : t('pagePostits.thisPostit');
      openModal(
        t('common.delete'),
        `<p>${t('pagePostits.delConfirm', { label })}</p>`,
        async () => {
          try {
            await api.deletePostit(pid);
            items.splice(
              items.findIndex((x) => x.id === pid),
              1,
            );
            closeModal();
            renderPostitList(items, items, userId);
          } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : t('common.error');
            toast(msg, 'error');
          }
        },
        t('common.delete'),
      );
    });
  });

  list.querySelectorAll<HTMLButtonElement>('.pi-color-swatch').forEach((btn) => {
    btn.addEventListener('click', () => {
      void (async () => {
        const pid = parseInt(btn.dataset['pid'] || '0');
        const color = btn.dataset['color'];
        const p = items.find((x) => x.id === pid);
        if (!p || !color || p.color === color) return;
        const row = btn.closest<HTMLElement>('.postit-row');
        const siblings = row?.querySelectorAll<HTMLElement>(`.pi-color-swatch[data-pid="${pid}"]`);
        const prevColor = p.color;
        if (row) row.dataset['color'] = color;
        siblings?.forEach((s) => s.classList.toggle('selected', s.dataset['color'] === color));
        try {
          const updated = (await api.updatePostit(pid, {
            description: p.description,
            date: p.date,
            color,
          })) as Postit;
          p.color = updated.color ?? null;
        } catch (err: unknown) {
          if (row) row.dataset['color'] = prevColor ?? 'yellow';
          siblings?.forEach((s) =>
            s.classList.toggle('selected', s.dataset['color'] === prevColor),
          );
          const msg = err instanceof Error ? err.message : t('common.error');
          toast(msg, 'error');
        }
      })();
    });
  });

  initPostitDragDrop(list, items, userId);
}

function initPostitDragDrop(list: HTMLElement, items: Postit[], _userId: number | null): void {
  const DRAG_THRESHOLD = 6;
  let dragSrc: HTMLElement | null = null;
  let pointerId: number | null = null;
  let startX = 0;
  let startY = 0;
  let started = false;
  let lastTarget: HTMLElement | null = null;
  let lastInsertBefore = true;

  const dropIndicator = document.createElement('div');
  dropIndicator.className = 'postit-drop-indicator';
  dropIndicator.style.cssText =
    'height:2px;background:var(--accent);border-radius:1px;margin:1px 0;display:none';

  function clearIndicator(): void {
    if (dropIndicator.parentElement) dropIndicator.parentElement.removeChild(dropIndicator);
    dropIndicator.style.display = 'none';
  }

  function showIndicator(target: HTMLElement | null, insertBefore: boolean): void {
    if (!target || !target.parentElement) return clearIndicator();
    dropIndicator.style.display = '';
    if (insertBefore) target.parentElement.insertBefore(dropIndicator, target);
    else target.parentElement.insertBefore(dropIndicator, target.nextSibling);
  }

  list.addEventListener('pointerdown', (e: PointerEvent) => {
    const target = e.target as HTMLElement | null;
    if (!target?.closest('.pi-handle')) return;
    const row = target.closest<HTMLElement>('.postit-row');
    if (!row) return;
    dragSrc = row;
    pointerId = e.pointerId;
    startX = e.clientX;
    startY = e.clientY;
    started = false;
    try {
      list.setPointerCapture(pointerId);
    } catch (err: unknown) {
      safeWarn('list.setPointerCapture (postit drag)', err);
    }
  });

  list.addEventListener('pointermove', (e: PointerEvent) => {
    if (!dragSrc || e.pointerId !== pointerId) return;
    if (!started) {
      const dx = Math.abs(e.clientX - startX);
      const dy = Math.abs(e.clientY - startY);
      if (dx < DRAG_THRESHOLD && dy < DRAG_THRESHOLD) return;
      started = true;
      dragSrc.style.opacity = '0.4';
    }
    e.preventDefault();
    const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
    const target = el ? el.closest<HTMLElement>('.postit-row') : null;
    let insertBefore = true;
    if (target && target !== dragSrc) {
      const rect = target.getBoundingClientRect();
      insertBefore = e.clientY < rect.top + rect.height / 2;
    }
    if (target !== lastTarget || insertBefore !== lastInsertBefore) {
      if (target && target !== dragSrc) {
        showIndicator(target, insertBefore);
      } else {
        clearIndicator();
      }
      lastTarget = target;
      lastInsertBefore = insertBefore;
    }
  });

  function finishDrag(): void {
    const src = dragSrc;
    const tgt = lastTarget;
    const insertBefore = lastInsertBefore;
    const wasStarted = started;
    if (pointerId != null) {
      try {
        list.releasePointerCapture(pointerId);
      } catch (err: unknown) {
        safeWarn('list.releasePointerCapture (postit drag)', err);
      }
    }
    dragSrc = null;
    pointerId = null;
    lastTarget = null;
    started = false;
    if (!src) return;
    src.style.opacity = '';
    clearIndicator();
    if (!wasStarted || !tgt || tgt === src) return;

    if (insertBefore) list.insertBefore(src, tgt);
    else list.insertBefore(src, tgt.nextSibling);
    const newOrder = [...list.querySelectorAll<HTMLElement>('.postit-row')].map((r) =>
      parseInt(r.dataset['pid'] || '0'),
    );
    const itemMap: Record<number, Postit> = Object.fromEntries(items.map((p) => [p.id, p]));
    items.splice(
      0,
      items.length,
      ...newOrder.map((id) => itemMap[id]).filter((p): p is Postit => Boolean(p)),
    );
    void (async () => {
      try {
        await api.reorderPostits(newOrder);
      } catch (err: unknown) {
        safeWarn('api.reorderPostits', err);
        safeToast(t('pagePostits.errReorder'), 'error');
      }
    })();
  }

  list.addEventListener('pointerup', finishDrag);
  list.addEventListener('pointercancel', finishDrag);
}

// ── ESM globals shim (lot B.8 + S5.3) ──
window.renderPostitsPage = renderPostitsPage;
window.PI_COLORS = PI_COLORS;
window.buildColorPicker = buildColorPicker;
window.buildPostitRow = buildPostitRow;
window.renderPostitList = renderPostitList;
window.wirePostitEvents = wirePostitEvents;
window.initPostitDragDrop = initPostitDragDrop;

export {};
